// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTheme, Skeleton, IconButton } from '@mui/material';
import { ReactComponent as TrashIcon } from '../assets/TrashIcon.svg';
import { ReactComponent as CirclePlusIcon } from '../assets/CirclePlusIcon.svg';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsButton from './SolyticsButton';
import SolyticsDropdownInput from './SolyticsDropdownInput';
import SolyticsInput from './SolyticsInput';
import SolyticsAutocomplete from './SolyticsAutocomplete';
import SolyticsTabs from './SolyticsTabs';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Clause options for advanced group headers.
 * Having and Where are the only valid SQL clause keywords.
 * Entity names (e.g. 'Artifact', 'UseCase') are NOT clauses — they are
 * passed via the fields schema as group field ids/labels.
 */
const CLAUSE_OPTIONS = [
  { value: 'Having', label: 'Having' },
  { value: 'Where', label: 'Where' },
];

/** Sort direction options — ORDER BY ASC / DESC */
const SORT_ORDER_OPTIONS = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Safe cross-environment unique id generator.
 * crypto.randomUUID() is not available on http origins (Storybook dev server).
 *
 * @returns {string}
 */
const genId = () => {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Returns the first operator value from a field definition, or ''.
 *
 * @param {Object|null|undefined} fieldDef
 * @returns {string}
 */
const getFirstOperator = (fieldDef) => fieldDef?.operators?.[0]?.value ?? '';

/**
 * Returns the default (empty) sort state.
 * sort: null means "sort not added". sort: object means "sort row is active".
 *
 * @returns {{ fieldId: null, order: null, dataType: null }}
 */
const defaultSort = () => ({ fieldId: null, order: null, dataType: null });

/**
 * Returns the default (empty) query value.
 *
 * @returns {{ logic: 'AND', rules: [], sort: null }}
 */
const defaultValue = () => ({ logic: 'AND', rules: [], sort: null });

// ---------------------------------------------------------------------------
// SortRule sub-component
// ---------------------------------------------------------------------------

/**
 * SortRule
 *
 * Renders the sort configuration row as part of the query builder.
 * Sort (ORDER BY) is a query concern — it belongs here alongside WHERE/HAVING.
 *
 * Layout: [Data Type ▼?]  [Sort By ▼]  [Order ▼]  [🗑]
 * The Data Type dropdown is only rendered when dataTypeOptions is non-empty,
 * as some consumers do not need to filter sortable fields by data type.
 *
 * This is a pure presentational sub-component — it owns no state.
 * All values and handlers are passed from SolyticsAdvancedFilter.
 *
 * @param {Object}   props
 * @param {Object}   props.sort             - Current sort: { fieldId, order, dataType }
 * @param {Array}    props.sortableFields   - Available sort fields: [{ id, label, dataType }]
 * @param {Array}    props.dataTypeOptions  - Data type filter options: [{ value, label }]
 * @param {Function} props.onSortChange     - (partialSort) => void
 * @param {Function} props.onRemove         - () => void
 */
const SortRule = ({
  sort,
  sortableFields,
  dataTypeOptions,
  onSortChange,
  onRemove,
}) => {
  const theme = useTheme();

  /**
   * When a data type is selected, filter sortable fields to only show fields
   * of that data type. When no data type is selected, show all sortable fields.
   */
  const filteredSortFields = useMemo(() => {
    if (!sort?.dataType) return sortableFields;
    return sortableFields.filter((f) => f.dataType === sort.dataType);
  }, [sortableFields, sort]);

  const sortFieldOptions = useMemo(
    () => filteredSortFields.map((f) => ({ value: f.id, label: f.label })),
    [filteredSortFields]
  );

  const hasDataTypeFilter = dataTypeOptions.length > 0;

  return (
    <SolyticsBox
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '12px',
        mb: '12px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        p: 1,
        flexWrap: 'wrap',
        backgroundColor:
          theme.palette?.advanceFilters
            ?.combinatorButtonBackgroundColorAdvTab ||
          theme.palette.background.default,
      }}
    >
      {/* Data Type — optional, only rendered when dataTypeOptions is provided */}
      {hasDataTypeFilter && (
        <SolyticsBox sx={{ minWidth: 150, flex: 1 }}>
          <SolyticsDropdownInput
            name="sort-data-type"
            label="Data Type"
            value={sort?.dataType || ''}
            options={dataTypeOptions}
            onChange={(e) =>
              // Changing data type resets field and order since they are
              // dependent on the selected data type
              onSortChange({
                dataType: e.target.value || null,
                fieldId: null,
                order: null,
              })
            }
            placeholder="Select data type"
          />
        </SolyticsBox>
      )}

      {/* Sort By */}
      <SolyticsBox sx={{ minWidth: 150, flex: 1 }}>
        <SolyticsDropdownInput
          name="sort-field"
          label="Sort By"
          value={sort?.fieldId || ''}
          options={sortFieldOptions}
          onChange={(e) =>
            // Changing the sort field resets order
            onSortChange({
              ...sort,
              fieldId: e.target.value || null,
              order: null,
            })
          }
          placeholder="Select attribute"
          disabled={hasDataTypeFilter && !sort?.dataType}
        />
      </SolyticsBox>

      {/* Sort Order */}
      <SolyticsBox sx={{ minWidth: 150, flex: 1 }}>
        <SolyticsDropdownInput
          name="sort-order"
          label="Sort Order"
          value={sort?.order || ''}
          options={SORT_ORDER_OPTIONS}
          onChange={(e) =>
            onSortChange({ ...sort, order: e.target.value || null })
          }
          placeholder="Select order"
          disabled={!sort?.fieldId}
        />
      </SolyticsBox>

      {/* Remove sort */}
      <SolyticsBox sx={{ mb: '4px', flexShrink: 0 }}>
        <IconButton onClick={onRemove} aria-label="Remove sort" size="small">
          <TrashIcon style={{ width: '20px', height: '20px' }} />
        </IconButton>
      </SolyticsBox>
    </SolyticsBox>
  );
};

// ---------------------------------------------------------------------------
// SolyticsAdvancedFilter
// ---------------------------------------------------------------------------

/**
 * SolyticsAdvancedFilter
 *
 * A pure, library-grade query builder component.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DESIGN PRINCIPLE
 * ─────────────────────────────────────────────────────────────────────────────
 * JSON schema in → render inputs → JSON out.
 *
 * This component has NO knowledge of:
 *   - Application domain concepts (what "Artifact" or "Team" means in your app)
 *   - Redux or any global store
 *   - Backend API shapes or transformation contracts
 *   - Any feature flags beyond what is necessary to render the UI
 *
 * All of that is the consuming container's responsibility.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIELDS SCHEMA  (props.fields)
 * ─────────────────────────────────────────────────────────────────────────────
 * An array of field definitions. Two types are supported:
 *
 * 1. Simple field — renders as [Attribute ▼ | Operator ▼ | Value | 🗑]
 *    {
 *      id:               string,
 *      label:            string,
 *      operators:        Array<{ value: string, label: string }>,
 *      getValueGetter?:  () => Promise<any[]>,
 *        — When present, the value input becomes an async-searchable autocomplete.
 *          The function receives an optional search string and returns a promise.
 *      formatOptions?:   (rawOptions: any[]) => Array<{ value, label }>,
 *        — Transforms raw getValueGetter results into autocomplete option shape.
 *    }
 *
 * 2. Group field — renders as an Advanced Rule block with Having/Where clause header
 *    {
 *      id:       string,           — entity identifier (e.g. 'Artifact', 'UseCase')
 *      label:    string,           — shown in the entity selector autocomplete
 *      isGroup:  true,
 *      fields:   SimpleField[],   — attributes available inside this group's nested rules
 *    }
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * VALUE SCHEMA  (props.value / onChange output)
 * ─────────────────────────────────────────────────────────────────────────────
 * A single consistent object is always produced — the shape never changes
 * based on internal state:
 *
 * {
 *   logic:  'AND' | 'OR',
 *
 *   rules: Array<SimpleRule | GroupRule>,
 *
 *   // SimpleRule
 *   // { id: string, isGroup: false, fieldId: string, operator: string, value: any }
 *
 *   // GroupRule (Advanced Rule block)
 *   // {
 *   //   id:           string,
 *   //   isGroup:      true,
 *   //   clause:       'Having' | 'Where',
 *   //   groupFieldId: string,
 *   //   rules:        Array<{ id: string, fieldId: string, operator: string, value: any }>
 *   // }
 *
 *   sort: null | {
 *     fieldId:  string | null,    — field to sort on (matches a sortableFields id)
 *     order:    'asc' | 'desc' | null,
 *     dataType: string | null,    — selected data type filter (when dataTypeOptions used)
 *   }
 *   // sort is null when no Sort Order row has been added.
 *   // sort is an object (possibly with null fields) when the row is active.
 * }
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY SORT IS PART OF THIS COMPONENT
 * ─────────────────────────────────────────────────────────────────────────────
 * Sort (ORDER BY) is part of a query just as WHERE and HAVING are. A complete
 * query has predicates (what rows to include) and ordering (how to present them).
 * Keeping sort here means the component produces one complete query value object —
 * no stitching required in the parent. It also means saved filters correctly
 * capture sort as part of the filter definition, which is the expected UX.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * @param {Object}    props
 *
 * @param {Array}     props.fields
 *   Field schema. Required. Mix of simple fields and group fields.
 *
 * @param {Object}    [props.value=null]
 *   Controlled query value. Matches VALUE SCHEMA above.
 *   Defaults to { logic: 'AND', rules: [], sort: null } when null/undefined.
 *
 * @param {Function}  [props.onChange]
 *   Fired on every change with the complete updated value.
 *   Signature: (value: QueryValue) => void
 *
 * @param {boolean}   [props.allowGroups=true]
 *   Whether to show the "Add Advanced Rule" button.
 *   Set to false for views where grouped rules are not applicable (e.g. temporal views).
 *
 * @param {Array}     [props.sortableFields=[]]
 *   Fields available for sort. Shape: [{ id: string, label: string, dataType: string }]
 *   When empty, the "Sort Order" button is not rendered — the feature is opt-in.
 *
 * @param {Array}     [props.dataTypeOptions=[]]
 *   Optional data type filter options for the sort row.
 *   Shape: [{ value: string, label: string }]
 *   When empty, the Data Type dropdown in SortRule is not rendered.
 *
 * @param {boolean}   [props.loading=false]
 *   When true, renders MUI Skeleton placeholders instead of the filter UI.
 *
 * @param {Object}    [props.sx]
 *   Additional MUI sx styles applied to the root SolyticsBox.
 */
const SolyticsAdvancedFilter = ({
  fields = [],
  value = null,
  onChange,
  allowGroups = true,
  sortableFields = [],
  dataTypeOptions = [],
  loading = false,
  sx,
}) => {
  const theme = useTheme();

  // -------------------------------------------------------------------------
  // Internal state
  // Initialised lazily from props.value. Synced via useEffect when props.value
  // changes externally (e.g. parent restores an edit-filter or resets the form).
  // -------------------------------------------------------------------------
  const [internalValue, setInternalValue] = useState(
    () => value || defaultValue()
  );

  useEffect(() => {
    setInternalValue(value || defaultValue());
  }, [value]);

  // -------------------------------------------------------------------------
  // Derived from the fields schema — all computed once when fields changes
  // -------------------------------------------------------------------------

  /** Simple (non-group) field definitions */
  const simpleFields = useMemo(
    () => fields.filter((f) => !f.isGroup),
    [fields]
  );

  /** Group field definitions */
  const groupFields = useMemo(() => fields.filter((f) => f.isGroup), [fields]);

  /** { value, label } options for the simple rule attribute autocomplete */
  const simpleFieldOptions = useMemo(
    () => simpleFields.map((f) => ({ value: f.id, label: f.label })),
    [simpleFields]
  );

  /** { value, label } options for the group entity autocomplete */
  const groupFieldOptions = useMemo(
    () => groupFields.map((f) => ({ value: f.id, label: f.label })),
    [groupFields]
  );

  /**
   * Operator options keyed by field id — O(1) lookup at render time.
   * Covers both top-level simple fields and nested fields inside group defs.
   */
  const operatorsByFieldId = useMemo(() => {
    const map = {};
    fields.forEach((f) => {
      if (!f.isGroup) {
        map[f.id] = (f.operators || []).map((op) => ({
          value: op.value,
          label: op.label,
        }));
      } else {
        (f.fields || []).forEach((nf) => {
          map[nf.id] = (nf.operators || []).map((op) => ({
            value: op.value,
            label: op.label,
          }));
        });
      }
    });
    return map;
  }, [fields]);

  /**
   * Field definition lookup by id.
   * Covers top-level simple fields AND nested fields inside group defs.
   * Used to determine whether to render an async autocomplete or plain input for values.
   */
  const fieldById = useMemo(() => {
    const map = {};
    fields.forEach((f) => {
      if (!f.isGroup) {
        map[f.id] = f;
      } else {
        (f.fields || []).forEach((nf) => {
          map[nf.id] = nf;
        });
      }
    });
    return map;
  }, [fields]);

  // -------------------------------------------------------------------------
  // Core update helper
  // Single path for all state mutations. Every handler calls update().
  // setInternalValue and onChange are never called directly anywhere else.
  // -------------------------------------------------------------------------

  /** Returns the current value, guaranteeing correct shape */
  const getCurrent = useCallback(
    () => internalValue || defaultValue(),
    [internalValue]
  );

  /**
   * Commits a new value to internal state and fires onChange.
   *
   * @param {Object} next - Full updated query value
   */
  const update = useCallback(
    (next) => {
      setInternalValue(next);
      onChange?.(next);
    },
    [onChange]
  );

  // -------------------------------------------------------------------------
  // AND / OR logic toggle
  // -------------------------------------------------------------------------

  /**
   * ToggleButtonGroup fires null when the user clicks the already-selected
   * button — guard against this to avoid clearing the logic value.
   */
  const handleLogicChange = useCallback(
    (newLogic) => {
      if (!newLogic) return;
      const current = getCurrent();
      update({ ...current, logic: newLogic });
    },
    [getCurrent, update]
  );

  // -------------------------------------------------------------------------
  // Simple rule handlers
  // -------------------------------------------------------------------------

  /**
   * Adds a new simple rule, pre-filled with the first available field and
   * its first operator so the user has something to build on immediately.
   */
  const handleAddSimpleRule = useCallback(() => {
    const firstField = simpleFields[0] ?? null;
    const newRule = {
      id: genId(),
      isGroup: false,
      fieldId: firstField?.id ?? '',
      operator: getFirstOperator(firstField),
      value: '',
    };
    const current = getCurrent();
    update({
      ...current,
      logic: current.logic || 'AND',
      rules: [...(current.rules || []), newRule],
    });
  }, [simpleFields, getCurrent, update]);

  /**
   * Removes a simple rule by id.
   *
   * @param {string} ruleId
   */
  const handleRemoveSimpleRule = useCallback(
    (ruleId) => {
      const current = getCurrent();
      update({
        ...current,
        rules: current.rules.filter((r) => r.id !== ruleId),
      });
    },
    [getCurrent, update]
  );

  /**
   * Applies a partial update to a simple rule.
   *
   * @param {string} ruleId
   * @param {Object} updates - Partial rule properties to merge
   */
  const handleUpdateSimpleRule = useCallback(
    (ruleId, updates) => {
      const current = getCurrent();
      update({
        ...current,
        rules: current.rules.map((r) =>
          r.id === ruleId ? { ...r, ...updates } : r
        ),
      });
    },
    [getCurrent, update]
  );

  /**
   * Handles attribute (field) change on a simple rule.
   * Resets operator to the first valid one for the new field and clears value —
   * equivalent to react-querybuilder's resetOnFieldChange behaviour.
   *
   * @param {string} ruleId
   * @param {string} newFieldId
   */
  const handleSimpleFieldChange = useCallback(
    (ruleId, newFieldId) => {
      const fieldDef = fieldById[newFieldId];
      handleUpdateSimpleRule(ruleId, {
        fieldId: newFieldId,
        operator: getFirstOperator(fieldDef),
        value: '',
      });
    },
    [fieldById, handleUpdateSimpleRule]
  );

  // -------------------------------------------------------------------------
  // Advanced group handlers
  // -------------------------------------------------------------------------

  /**
   * Adds a new advanced group rule.
   * Auto-populates the first available group entity and creates one empty nested
   * rule so the user can start filling in immediately without an extra click.
   */
  const handleAddGroup = useCallback(() => {
    const firstGroupField = groupFields[0] ?? null;
    const newGroup = {
      id: genId(),
      isGroup: true,
      clause: 'Having',
      groupFieldId: firstGroupField?.id ?? '',
      rules: [{ id: genId(), fieldId: '', operator: '', value: '' }],
    };
    const current = getCurrent();
    update({
      ...current,
      logic: current.logic || 'AND',
      rules: [...(current.rules || []), newGroup],
    });
  }, [groupFields, getCurrent, update]);

  /**
   * Removes an advanced group by id.
   *
   * @param {string} groupId
   */
  const handleRemoveGroup = useCallback(
    (groupId) => {
      const current = getCurrent();
      update({
        ...current,
        rules: current.rules.filter((r) => r.id !== groupId),
      });
    },
    [getCurrent, update]
  );

  /**
   * Applies a partial update to an advanced group.
   *
   * @param {string} groupId
   * @param {Object} updates
   */
  const handleUpdateGroup = useCallback(
    (groupId, updates) => {
      const current = getCurrent();
      update({
        ...current,
        rules: current.rules.map((r) =>
          r.id === groupId ? { ...r, ...updates } : r
        ),
      });
    },
    [getCurrent, update]
  );

  /**
   * Handles entity change on an advanced group.
   * Resets nested rules because the available attributes are entirely different
   * for a different entity (e.g. Artifact fields ≠ UseCase fields).
   *
   * @param {string} groupId
   * @param {string} newGroupFieldId
   */
  const handleGroupEntityChange = useCallback(
    (groupId, newGroupFieldId) => {
      handleUpdateGroup(groupId, { groupFieldId: newGroupFieldId, rules: [] });
    },
    [handleUpdateGroup]
  );

  // -------------------------------------------------------------------------
  // Nested rule handlers (rules inside an advanced group)
  // -------------------------------------------------------------------------

  /**
   * Adds an empty nested rule to an advanced group.
   *
   * @param {string} groupId
   */
  const handleAddNestedRule = useCallback(
    (groupId) => {
      const newRule = { id: genId(), fieldId: '', operator: '', value: '' };
      const current = getCurrent();
      update({
        ...current,
        rules: current.rules.map((r) =>
          r.id === groupId ? { ...r, rules: [...(r.rules || []), newRule] } : r
        ),
      });
    },
    [getCurrent, update]
  );

  /**
   * Removes a nested rule from an advanced group.
   * If the group has no nested rules remaining after removal, the entire group
   * is also removed — orphaned empty group blocks are never shown.
   *
   * @param {string} groupId
   * @param {string} nestedId
   */
  const handleRemoveNestedRule = useCallback(
    (groupId, nestedId) => {
      const current = getCurrent();

      const mapped = current.rules.map((r) => {
        if (r.id !== groupId) return r;
        return {
          ...r,
          rules: (r.rules || []).filter((nr) => nr.id !== nestedId),
        };
      });

      // Auto-remove any group that now has zero nested rules
      const final = mapped.filter(
        (r) => !r.isGroup || (r.rules && r.rules.length > 0)
      );

      update({ ...current, rules: final });
    },
    [getCurrent, update]
  );

  /**
   * Applies a partial update to a nested rule.
   *
   * @param {string} groupId
   * @param {string} nestedId
   * @param {Object} updates
   */
  const handleUpdateNestedRule = useCallback(
    (groupId, nestedId, updates) => {
      const current = getCurrent();
      update({
        ...current,
        rules: current.rules.map((r) =>
          r.id === groupId
            ? {
                ...r,
                rules: (r.rules || []).map((nr) =>
                  nr.id === nestedId ? { ...nr, ...updates } : nr
                ),
              }
            : r
        ),
      });
    },
    [getCurrent, update]
  );

  /**
   * Handles field change on a nested rule.
   * Resets operator and value — same resetOnFieldChange behaviour as simple rules.
   *
   * @param {string} groupId
   * @param {string} nestedId
   * @param {string} newFieldId
   */
  const handleNestedFieldChange = useCallback(
    (groupId, nestedId, newFieldId) => {
      const fieldDef = fieldById[newFieldId];
      handleUpdateNestedRule(groupId, nestedId, {
        fieldId: newFieldId,
        operator: getFirstOperator(fieldDef),
        value: '',
      });
    },
    [fieldById, handleUpdateNestedRule]
  );

  // -------------------------------------------------------------------------
  // Sort handlers
  // -------------------------------------------------------------------------

  /**
   * Activates the sort row by initialising sort state to an empty sort object.
   * Transition: sort: null → sort: { fieldId: null, order: null, dataType: null }
   */
  const handleAddSort = useCallback(() => {
    const current = getCurrent();
    update({ ...current, sort: defaultSort() });
  }, [getCurrent, update]);

  /**
   * Removes sort from the query entirely.
   * Transition: sort: { ... } → sort: null
   */
  const handleRemoveSort = useCallback(() => {
    const current = getCurrent();
    update({ ...current, sort: null });
  }, [getCurrent, update]);

  /**
   * Merges a partial sort update into current sort state.
   * Always spreads over defaultSort() to ensure a complete sort shape.
   *
   * @param {Object} partialSort
   */
  const handleSortChange = useCallback(
    (partialSort) => {
      const current = getCurrent();
      update({ ...current, sort: { ...defaultSort(), ...partialSort } });
    },
    [getCurrent, update]
  );

  // -------------------------------------------------------------------------
  // Render helper: value input
  // Shared by simple rules and nested rules.
  // Renders SolyticsAutocomplete (async) when field has getValueGetter,
  // otherwise renders plain SolyticsInput.
  // -------------------------------------------------------------------------

  /**
   * @param {string}   fieldId
   * @param {any}      currentValue
   * @param {Function} onValueChange  - (newValue: any) => void
   * @param {string}   namePrefix     - Unique input name prefix
   * @returns {React.ReactElement}
   */
  const renderValueInput = useCallback(
    (fieldId, currentValue, onValueChange, namePrefix) => {
      const fieldDef = fieldById[fieldId];
      if (fieldDef?.getValueGetter) {
        return (
          <SolyticsAutocomplete
            name={`${namePrefix}-value`}
            label="Value"
            value={currentValue || null}
            onChange={onValueChange}
            placeholder="Select value"
            controlled
            fetchData={fieldDef.getValueGetter}
            formatOptions={fieldDef.formatOptions}
            fullWidth
          />
        );
      }
      return (
        <SolyticsInput
          name={`${namePrefix}-value`}
          label="Value"
          value={currentValue || ''}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Enter value"
          fullWidth
        />
      );
    },
    [fieldById]
  );

  // -------------------------------------------------------------------------
  // Render: simple rule row
  // Layout: [Attribute ▼]  [Operator ▼]  [Value]  [🗑]
  // -------------------------------------------------------------------------
  const renderSimpleRule = useCallback(
    (rule) => {
      const operatorOptions = operatorsByFieldId[rule.fieldId] || [];
      const selectedAttr =
        simpleFieldOptions.find((o) => o.value === rule.fieldId) || null;

      return (
        <SolyticsBox
          key={rule.id}
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-end',
            borderRadius: '8px',
          }}
        >
          {/* Attribute — searchable autocomplete */}
          <SolyticsBox sx={{ flex: 1, minWidth: 0 }}>
            <SolyticsAutocomplete
              name={`attr-${rule.id}`}
              label="Attribute"
              options={simpleFieldOptions}
              value={selectedAttr}
              onChange={(opt) =>
                handleSimpleFieldChange(rule.id, opt?.value ?? '')
              }
              placeholder="Select attribute"
              controlled={false}
              fullWidth
            />
          </SolyticsBox>

          {/* Operator */}
          <SolyticsBox sx={{ flex: 1 }}>
            <SolyticsDropdownInput
              name={`op-${rule.id}`}
              label="Operator"
              value={rule.operator || ''}
              options={operatorOptions}
              onChange={(e) =>
                handleUpdateSimpleRule(rule.id, { operator: e.target.value })
              }
              placeholder="Select operator"
              disabled={!rule.fieldId}
            />
          </SolyticsBox>

          {/* Value */}
          <SolyticsBox
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {renderValueInput(
              rule.fieldId,
              rule.value,
              (val) => handleUpdateSimpleRule(rule.id, { value: val }),
              `simple-${rule.id}`
            )}
          </SolyticsBox>

          {/* Remove */}
          <SolyticsBox sx={{ flexShrink: 0, mb: '4px' }}>
            <IconButton
              size="small"
              onClick={() => handleRemoveSimpleRule(rule.id)}
              aria-label="Remove rule"
            >
              <TrashIcon style={{ width: '20px', height: '20px' }} />
            </IconButton>
          </SolyticsBox>
        </SolyticsBox>
      );
    },
    [
      simpleFieldOptions,
      operatorsByFieldId,
      handleSimpleFieldChange,
      handleUpdateSimpleRule,
      handleRemoveSimpleRule,
      renderValueInput,
    ]
  );

  // -------------------------------------------------------------------------
  // Render: advanced group block
  // Header: [Having ▼]  [Entity ▼]  [Where ▼]  [🗑]
  // Body:   nested rule rows + "Add Rule" button
  // -------------------------------------------------------------------------
  const renderGroup = useCallback(
    (group) => {
      const groupFieldDef = groupFields.find(
        (f) => f.id === group.groupFieldId
      );
      const nestedFieldOptions = (groupFieldDef?.fields || []).map((f) => ({
        value: f.id,
        label: f.label,
      }));

      const selectedGroupOpt =
        groupFieldOptions.find(
          (o) =>
            o.value?.toLowerCase() === (group.groupFieldId || '').toLowerCase()
        ) || null;

      return (
        <SolyticsBox
          key={group.id}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            p: 1,
            mb: '12px',
          }}
        >
          {/* ---- Group Header ---- */}
          <SolyticsBox
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '12px',
              mb: 2,
              minHeight: '64px',
              borderRadius: '8px',
              backgroundColor:
                theme.palette?.advanceFilters?.advanceBlockHeaderColor ||
                theme.palette.grey?.[40] ||
                theme.palette.background.default,
              padding: '12px',
            }}
          >
            {/* Left clause dropdown — shows Having option */}
            <SolyticsBox sx={{ flex: 1, minWidth: 0 }}>
              <SolyticsDropdownInput
                name={`clause-left-${group.id}`}
                label="Clause"
                value={group.clause === 'Having' ? 'Having' : ''}
                options={CLAUSE_OPTIONS.filter((c) => c.value === 'Having')}
                onChange={(e) =>
                  handleUpdateGroup(group.id, { clause: e.target.value })
                }
              />
            </SolyticsBox>

            {/* Entity selector — searchable autocomplete */}
            <SolyticsBox sx={{ flex: 1, minWidth: 0 }}>
              <SolyticsAutocomplete
                name={`group-entity-${group.id}`}
                label="Entity"
                options={groupFieldOptions}
                value={selectedGroupOpt}
                onChange={(opt) =>
                  handleGroupEntityChange(group.id, opt?.value ?? '')
                }
                placeholder="Select entity"
                controlled={false}
                fullWidth
              />
            </SolyticsBox>

            {/* Right clause dropdown — shows Where option */}
            <SolyticsBox sx={{ flex: 1, minWidth: 0 }}>
              <SolyticsDropdownInput
                name={`clause-right-${group.id}`}
                label="Clause"
                value={group.clause === 'Where' ? 'Where' : ''}
                options={CLAUSE_OPTIONS.filter((c) => c.value === 'Where')}
                onChange={(e) =>
                  handleUpdateGroup(group.id, { clause: e.target.value })
                }
              />
            </SolyticsBox>

            {/* Remove group */}
            <SolyticsBox sx={{ flexShrink: 0, mb: '4px' }}>
              <IconButton
                size="small"
                onClick={() => handleRemoveGroup(group.id)}
                aria-label="Remove group"
              >
                <TrashIcon style={{ width: '16px', height: '16px' }} />
              </IconButton>
            </SolyticsBox>
          </SolyticsBox>

          {/* ---- Nested Rules ---- */}
          <SolyticsBox
            sx={{
              borderRadius: '8px',
              p: 1,
              backgroundColor:
                theme.palette?.advanceFilters
                  ?.combinatorButtonBackgroundColorAdvTab ||
                theme.palette.background.default,
            }}
          >
            {(group.rules || []).length === 0 && (
              <SolyticsTypography
                variant="body2"
                color="text.secondary"
                sx={{ p: 1 }}
              >
                No conditions yet. Click &quot;Add Rule&quot; below.
              </SolyticsTypography>
            )}

            {(group.rules || []).map((nested) => {
              const nestedOpOptions = operatorsByFieldId[nested.fieldId] || [];
              const selectedNestedAttr =
                nestedFieldOptions.find((o) => o.value === nested.fieldId) ||
                null;

              return (
                <SolyticsBox
                  key={nested.id}
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-end',
                    mb: '8px',
                  }}
                >
                  {/* Nested attribute */}
                  <SolyticsBox sx={{ flex: 1, minWidth: 0 }}>
                    <SolyticsAutocomplete
                      name={`nested-attr-${nested.id}`}
                      label="Attribute"
                      options={nestedFieldOptions}
                      value={selectedNestedAttr}
                      onChange={(opt) =>
                        handleNestedFieldChange(
                          group.id,
                          nested.id,
                          opt?.value ?? ''
                        )
                      }
                      placeholder="Select attribute"
                      controlled={false}
                      fullWidth
                    />
                  </SolyticsBox>

                  {/* Nested operator */}
                  <SolyticsBox sx={{ flex: 1 }}>
                    <SolyticsDropdownInput
                      name={`nested-op-${nested.id}`}
                      label="Operator"
                      value={nested.operator || ''}
                      options={nestedOpOptions}
                      onChange={(e) =>
                        handleUpdateNestedRule(group.id, nested.id, {
                          operator: e.target.value,
                        })
                      }
                      placeholder="Select operator"
                      disabled={!nested.fieldId}
                    />
                  </SolyticsBox>

                  {/* Nested value */}
                  <SolyticsBox
                    sx={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {renderValueInput(
                      nested.fieldId,
                      nested.value,
                      (val) =>
                        handleUpdateNestedRule(group.id, nested.id, {
                          value: val,
                        }),
                      `nested-${nested.id}`
                    )}
                  </SolyticsBox>

                  {/* Remove nested rule */}
                  <SolyticsBox sx={{ flexShrink: 0, mb: '4px' }}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleRemoveNestedRule(group.id, nested.id)
                      }
                      aria-label="Remove condition"
                    >
                      <TrashIcon style={{ width: '16px', height: '16px' }} />
                    </IconButton>
                  </SolyticsBox>
                </SolyticsBox>
              );
            })}

            {/* Add nested rule */}
            <SolyticsBox sx={{ p: 0.3, mt: 0.6 }}>
              <SolyticsButton
                startIcon={
                  <CirclePlusIcon style={{ width: '16px', height: '16px' }} />
                }
                onClick={() => handleAddNestedRule(group.id)}
                variant="outlined"
                size="small"
                sx={{
                  height: '40px',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                }}
              >
                Add Rule
              </SolyticsButton>
            </SolyticsBox>
          </SolyticsBox>
        </SolyticsBox>
      );
    },
    [
      groupFields,
      groupFieldOptions,
      operatorsByFieldId,
      handleUpdateGroup,
      handleGroupEntityChange,
      handleRemoveGroup,
      handleAddNestedRule,
      handleRemoveNestedRule,
      handleUpdateNestedRule,
      handleNestedFieldChange,
      renderValueInput,
      theme,
    ]
  );

  // -------------------------------------------------------------------------
  // Derived rendering flags
  // -------------------------------------------------------------------------
  const current = getCurrent();
  const rules = current.rules || [];
  const hasRules = rules.length > 0;
  const hasGroupFields = groupFields.length > 0;
  const hasSortableFields = sortableFields.length > 0;
  /** Sort is "active" when sort is a non-null object (even if fields not filled yet) */
  const sortActive = current.sort !== null && current.sort !== undefined;

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <SolyticsBox
        sx={{
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          ...sx,
        }}
      >
        <SolyticsBox sx={{ display: 'flex', flexDirection: 'column' }}>
          <Skeleton variant="text" width={300} height={24} />
        </SolyticsBox>
        <SolyticsBox
          sx={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: 'center',
            pt: '4px',
          }}
        >
          <Skeleton
            variant="rectangular"
            width={110}
            height={40}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={170}
            height={40}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={110}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </SolyticsBox>
      </SolyticsBox>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <SolyticsBox
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...sx,
      }}
    >
      {!hasRules && !sortActive && (
        <SolyticsTypography variant="body1" color="text.secondary">
          No rules added yet. Click &quot;Add Rule&quot; to get started.
        </SolyticsTypography>
      )}
      {/* AND / OR toggle — only visible when at least one rule exists */}
      {hasRules && (
        <>
          <SolyticsBox
            sx={{
              alignSelf: 'flex-start',
              flexShrink: 0,
            }}
          >
            <SolyticsTabs
              variant="segmented"
              size="small"
              value={current.logic || 'AND'}
              onChange={handleLogicChange}
              items={[
                { label: 'AND', value: 'AND' },
                { label: 'OR', value: 'OR' },
              ]}
            />
          </SolyticsBox>

          {/* Scrollable content area */}
          <SolyticsBox
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pr: 2,
            }}
          >
            {/* Rules list */}
            <SolyticsBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              {rules.map((rule) =>
                rule.isGroup ? renderGroup(rule) : renderSimpleRule(rule)
              )}
            </SolyticsBox>

            {/* Sort row — rendered when sort has been activated */}
            {sortActive && (
              <SortRule
                sort={current.sort}
                sortableFields={sortableFields}
                dataTypeOptions={dataTypeOptions}
                onSortChange={handleSortChange}
                onRemove={handleRemoveSort}
              />
            )}
          </SolyticsBox>
        </>
      )}

      {/* Action buttons */}
      <SolyticsBox
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Add Rule — always shown */}
        <SolyticsButton
          startIcon={
            <CirclePlusIcon style={{ width: '16px', height: '16px' }} />
          }
          onClick={handleAddSimpleRule}
          variant="contained"
        >
          Add Rule
        </SolyticsButton>

        {/*
              Add Advanced Rule — shown only when:
                (a) allowGroups is true  (set by the consumer via the allowGroups prop)
                (b) at least one group field exists in the schema
              Both conditions must hold. If the schema has no group fields, this button
              never appears regardless of allowGroups.
            */}
        {allowGroups && hasGroupFields && (
          <SolyticsButton
            startIcon={
              <CirclePlusIcon style={{ width: '16px', height: '16px' }} />
            }
            onClick={handleAddGroup}
            variant="outlined"
          >
            Add Advanced Rule
          </SolyticsButton>
        )}

        {/*
              Sort Order — shown only when:
                (a) sortableFields has at least one entry  (opt-in feature)
                (b) sort is not already active
              Sort is part of the query (ORDER BY), so it lives here alongside
              the predicate builders, not in a separate parent component.
            */}
        {hasSortableFields && !sortActive && (
          <SolyticsButton onClick={handleAddSort} variant="outlined">
            Sort Order
          </SolyticsButton>
        )}
      </SolyticsBox>
    </SolyticsBox>
  );
};

/**
 * SolyticsAdvancedFilter
 *
 * A pure, recursive query builder component. It takes a field schema and a
 * query value, and emits a new query value on every change.
 *
 * It supports:
 * - Simple rules (field, operator, value)
 * - Group rules (Having/Where clauses with nested rules)
 * - Sort (ORDER BY) configuration
 * - Async value fetching via `getValueGetter`
 */

export default SolyticsAdvancedFilter;
